import React from "react";
import { GraduationCap, Building } from "lucide-react";
import { IDoctor } from "../../../../dto/interfaces";
import { Button } from "../../../../components/common/button";
import { assets } from "../../../../assets/assets";

interface DoctorProfileParams {
    doctor: IDoctor;
}

const AboutDoctorSection: React.FC<DoctorProfileParams> = ({ doctor }) => {
    const credentials = `${doctor.degree} | ${doctor.designation}`;
    const aboutImageUrl = doctor.profileImage;
    const bioFull =
        doctor?.about || `Specializing in ${doctor.speciality}. With ${doctor.experience} years of experience.`;

    const onBookAppointment = () => {
        // Find the booking section element by its ID
        const bookingSection = document.getElementById("book-appointment");

        if (bookingSection) {
            // Use the browser's built-in smooth scroll
            bookingSection.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            // Fallback if the element isn't found
            console.warn("Booking section with id 'book-appointment' not found.");
        }
    };

    return (
        <section id="about-doctor" className="py-16 md:py-24 bg-gray-50">
            <div className="container mx-auto px-6">
                <div className="flex flex-col lg:flex-row bg-white rounded-[32px] shadow-sm overflow-hidden">
                    {/* === Left Side: Text Content === */}
                    <div className="flex-1 p-8 md:p-12 lg:p-14 flex flex-col items-center md:items-start">
                        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                            About Doctor
                        </span>
                        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-2 mb-2 w-full text-center md:text-left">Dr. {doctor.name}</h2>
                        <p className="text-lg font-medium text-blue-600 mb-6 text-center md:text-left">{credentials}</p>
                        <p className="text-gray-700 text-base leading-relaxed text-center md:text-left mb-8">{bioFull}</p>

                        {/* Info Blocks */}
                        <div className="flex flex-col sm:flex-row gap-6 mb-10">
                            {/* Education Block */}
                            <div className="flex items-start w-full gap-4">
                                <div className="w-12 h-12 bg-gray-100 flex-shrink-0 flex items-center justify-center rounded-lg">
                                    <GraduationCap className="w-6 h-6 text-gray-700" />
                                </div>
                                <div>
                                    <span className="block text-base font-semibold text-gray-900">Education</span>
                                    <span className="block text-sm text-gray-600">{doctor.degree}</span>
                                </div>
                            </div>

                            {/* Affiliation Block */}
                            <div className="flex items-start w-full gap-4">
                                <div className="w-12 h-12 bg-gray-100 flex-shrink-0 flex items-center justify-center rounded-lg">
                                    <Building className="w-6 h-6 text-gray-700" />
                                </div>
                                <div>
                                    <span className="block text-base font-semibold text-gray-900">
                                        {doctor.designation || "Professional"}
                                    </span>
                                    <span className="block text-sm text-gray-600">{doctor.clinicName}</span>
                                </div>
                            </div>
                        </div>

                        {/* Book Appointment Button */}
                        <Button onClick={onBookAppointment} size="md">
                            Book Appointment
                        </Button>
                    </div>

                    {/* === Right Side: Image === */}
                    <div className="flex-none lg:w-2/5 hidden md:block">
                        <img
                            src={aboutImageUrl || assets?.placeholder_avatar_image}
                            alt={`Portrait of ${doctor.name}`}
                            className="w-full h-full object-cover min-h-[300px] lg:min-h-full"
                            loading="eager"
                            decoding="sync"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutDoctorSection;
